"use client"
import { useState, useEffect } from "react"
import DateObject from "react-date-object"
import persian from "react-date-object/calendars/persian"
import persian_fa from "react-date-object/locales/persian_fa"
import gregorian from "react-date-object/calendars/gregorian"
import gregorian_fa from "react-date-object/locales/gregorian_fa"
import arabic from "react-date-object/calendars/arabic"
import arabic_fa from "react-date-object/locales/arabic_fa"
import { WheelPicker, WheelPickerWrapper } from "./wheel-picker"
import { Button } from "./ui/button"
import { CornerDownLeft } from "lucide-react"
import { diffInYMD } from "@/lib/utils"

const DateConverter = () => {
  const [calendar, setCalendar] = useState<"persian" | "gregorian" | "arabic">(
    "persian"
  )
  const [showResult, setShowResult] = useState(false)

  const [date, setDate] = useState(
    new DateObject({ calendar: persian, locale: persian_fa })
  )

  // وقتی تقویم عوض شد، تاریخ امروز همون تقویم ست میشه
  useEffect(() => {
    if (calendar === "persian") {
      setDate(new DateObject({ calendar: persian, locale: persian_fa }))
    } else if (calendar === "gregorian") {
      setDate(new DateObject({ calendar: gregorian, locale: gregorian_fa }))
    } else {
      setDate(new DateObject({ calendar: arabic, locale: arabic_fa }))
    }
  }, [calendar])

  // ماه‌ها
  const persianMonths = [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند",
  ]
  const gregorianMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]
  const arabicMonths = [
    "محرم",
    "صفر",
    "ربیع‌الاول",
    "ربیع‌الثانی",
    "جمادی‌الاول",
    "جمادی‌الثانی",
    "رجب",
    "شعبان",
    "رمضان",
    "شوال",
    "ذیقعده",
    "ذیحجه",
  ]

  const getMonths = () => {
    if (calendar === "persian") return persianMonths
    if (calendar === "gregorian") return gregorianMonths
    return arabicMonths
  }

  // تاریخ پایه برای تبدیل
  const baseDate = date
  const persianDate = new DateObject({
    date: baseDate,
  })
    .convert(persian)
    .setLocale(persian_fa)
  const gregorianDate = new DateObject({
    date: baseDate,
  })
    .convert(gregorian)
    .setLocale(gregorian_fa)
  const arabicDate = new DateObject({
    date: baseDate,
  })
    .convert(arabic)
    .setLocale(arabic_fa)

  const age = diffInYMD(baseDate)

  return (
    <div className="rounded-xl p-4 w-full relative">
      {/* انتخاب نوع تقویم */}
      <div
        className={`absolute inset-0 bg-background ${showResult ? "block" : "hidden"} z-50`}
      >
        <div className="flex flex-col divide-border divide-y">
          <div className="flex justify-between py-4 items-center">
            <p>شمسی</p>
            <p>
              {`${persianDate.day} ${persianDate.month.name} ${persianDate.year}`}
            </p>
          </div>
          <div className="flex justify-between py-4 items-center">
            <p>قمری</p>
            <p>
              {`${arabicDate.day} ${arabicDate.month.name} ${arabicDate.year}`}
            </p>
          </div>

          <div className="flex justify-between py-4 items-center">
            <p>میلادی</p>
            <p>
              {`${gregorianDate.day} ${gregorianDate.month.name} ${gregorianDate.year}`}
            </p>
          </div>

          <div className="py-4">{persianDate.weekDay.name}</div>

          <div className="py-4 flex justify-between items-center">
            <p>فاصله زمانی (سن)</p>

            {age.years > 0 && `${age.years} سال ${age.months ? age.months + " ماه " : ""}${age.days ? age.days + " روز" : ""}`}
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowResult(false)}
        >
          تبدیل تاریخ جدید
        </Button>
      </div>
      <div className="flex justify-between items-center gap-2 mb-4">
        <p>تاریخ مبدا : </p>
        <div className="flex gap-2">
          <button
            onClick={() => setCalendar("persian")}
            className={`px-2 py-1 rounded cursor-pointer ${calendar === "persian" ? "bg-primary text-white" : "bg-accent"}`}
          >
            شمسی
          </button>
          <button
            onClick={() => setCalendar("arabic")}
            className={`px-2 py-1 rounded cursor-pointer ${calendar === "arabic" ? "bg-primary text-white" : "bg-accent"}`}
          >
            قمری
          </button>
          <button
            onClick={() => setCalendar("gregorian")}
            className={`px-2 py-1 rounded cursor-pointer ${calendar === "gregorian" ? "bg-primary text-white" : "bg-accent"}`}
          >
            میلادی
          </button>
        </div>
      </div>

      {/* انتخاب روز/ماه/سال */}
      <div className="flex mb-4">
        <WheelPickerWrapper>
          <WheelPicker
            value={`${date.day}`}
            onValueChange={(value) => {
              const newDate = new DateObject({
                day: Number(value),
                year: date.year,
                month: date.month.number,
                calendar: date.calendar,
              })
              console.log(newDate)
              setDate(newDate)
            }}
            options={Array.from(
              { length: date.month.length },
              (_, i) => i + 1
            ).map((d) => ({
              label: d,
              value: `${d}`,
            }))}
          ></WheelPicker>
          <WheelPicker
            scrollSensitivity={50}
            value={`${date.month.number}`}
            onValueChange={(value) => {
              setDate(
                new DateObject({
                  month: Number(value),
                  year: date.year,
                  calendar: date.calendar,
                  day: date.day,
                })
              )
            }}
            options={getMonths().map((m, i) => ({
              label: m,
              value: `${i + 1}`,
            }))}
          />
          <WheelPicker
            value={`${date.year}`}
            onValueChange={(value) => {
              setDate(
                new DateObject({
                  year: Number(value),
                  month: date.month.number,
                  day: date.day,
                  calendar: date.calendar,
                })
              )
            }}
            options={Array.from(
              { length: 60 },
              (_, i) => date.year - 30 + i
            ).map((m) => ({ label: m, value: `${m}` }))}
          />
        </WheelPickerWrapper>
        {/* روز
        <select
          className="bg-gray-800 p-2 rounded"
          value={date.day}
          onChange={(e) =>
            setDate(new DateObject({ day: Number(e.target.value), year: date.year, month: date.month.number }) )
          }
        >
          {Array.from({ length: date.month.length }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select> */}

        {/* ماه
        <select
          className="bg-gray-800 p-2 rounded"
          value={date.month.number}
          onChange={(e) =>
            setDate(new DateObject({ ...date, month: Number(e.target.value) }))
          }
        >
          {getMonths().map((m, i) => (
            <option key={i} value={i + 1}>
              {m}
            </option>
          ))}
        </select> */}

        {/* سال
        <select
          className="bg-gray-800 p-2 rounded"
          value={date.year}
          onChange={(e) =>
            setDate(new DateObject({ year: Number(e.target.value), month: date.month.number, day: date.day }) )
          }
        >
          {Array.from({ length: 60 }, (_, i) => date.year - 30 + i).map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select> */}
      </div>
      <Button className="w-full" onClick={() => setShowResult(true)}>
        تبدیل تاریخ
        <CornerDownLeft />
      </Button>
    </div>
  )
}

export default DateConverter
